import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { me } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { useAuth } from '../stores/auth.js';

/**
 * Route guard reception: chiama /auth/me al mount; se 401 redirige a /login
 * con `?from=<path-corrente>` query param cosi' il post-login redirect porta
 * l'utente esattamente dove voleva andare. Pattern bookmarkable (cluster
 * login PIN HM-385). state.from come fallback per backward compat.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const setUser = useAuth((s) => s.setUser);
  const user = useAuth((s) => s.user);
  const location = useLocation();

  // QA sweep 2026-07-22 (bug BASSO): niente GET /auth/me al boot senza un
  // hint di sessione — la cache user persistita (hm-auth-cache) e' settata
  // al login riuscito e rimossa al logout/401 (handler globale App.tsx).
  // Prima ogni load pre-login sparava /auth/me → 401 in console. Senza
  // hint si va diretti a /login senza chiamata; la verita' resta comunque
  // il cookie HttpOnly, validato da /auth/me quando l'hint c'e'.
  const hasSessionHint = user !== null;

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const res = await me();
      setUser(res.user);
      return res.user;
    },
    enabled: hasSessionHint,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 1;
    },
    staleTime: 60_000,
  });

  if (!hasSessionHint || query.isError) {
    const from = location.pathname + (location.search ?? '');
    const target = `/login?from=${encodeURIComponent(from)}`;
    return <Navigate to={target} replace state={{ from }} />;
  }
  if (query.isLoading && !user) {
    return <FullScreenSpinner label="Verifica sessione…" />;
  }
  return <>{children}</>;
}

function FullScreenSpinner({ label }: { label: string }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        fontFamily: 'system-ui, sans-serif',
        background: '#0f172a',
        color: '#f8fafc',
      }}
    >
      <p>{label}</p>
    </div>
  );
}
